import { ArrestDesk } from "../enterprise/officer/ArrestDesk";
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsProsecutorDashboard, ujmsProsecutorCaseload, ujmsProsecutorTeam,
  ujmsAssignProsecutor, ujmsReferralDetail, ujmsProsecutorAutoAssign,
} from "../../services/ujmsApi";
import { Panel, Stat, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, fmtDT, fmtD, cls, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Briefcase, Scale, UserPlus } from "lucide-react";

const CASE_FILTERS = ["Mine", "Unassigned", "All"];

export default function ProsecutorWorkspace() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { ujmsProsecutorDashboard().then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="prosecutor-workspace">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Prosecutor Workspace</h1>
        <p className="text-sm text-slate-500">Charging review, caseload assignments and privileged work product for the State's Attorney's Office.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Pending Charging Decisions" value={d?.pending_referrals_count} testId="pw-stat-referrals" tone={d?.pending_referrals_count ? "warn" : undefined} />
        <Stat label="My Caseload" value={d?.my_caseload_count} testId="pw-stat-caseload" />
        <Stat label="Hearings (7 days)" value={d?.hearings_7d_count} testId="pw-stat-hearings" />
        <Stat label="Open Tasks" value={d?.open_tasks_count} testId="pw-stat-tasks" />
      </div>

      <section data-testid="pw-charging-arrests"><ArrestDesk /></section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 space-y-4">
          <ChargingQueue referrals={d?.pending_referrals || []} />
          <CaseloadPanel onChanged={load} />
        </div>
        <div className="space-y-4">
          <Panel title="Upcoming hearings (7 days)" testId="pw-hearings">
            {(d?.upcoming_hearings || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No hearings scheduled.</div>
            ) : (
              <table className="w-full border-collapse">
                <tbody>
                  {(d?.upcoming_hearings || []).map((h) => (
                    <tr key={h.id} className="hover:bg-slate-50">
                      <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(h.scheduled_start)}</td>
                      <td className={tdCls}>
                        <Link className="text-blue-900 hover:underline font-mono text-xs" to={`/ujms/cases/${h.case_id}`}>{h.case_number}</Link>
                        <div className="text-xs text-slate-500">{h.hearing_type_label || h.hearing_type}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <Panel title="Recent charging decisions" testId="pw-decisions">
            {(d?.recent_decisions || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">No decisions yet.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.recent_decisions || []).map((r) => (
                  <div key={r.id} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                    <div>
                      <div className="text-slate-800">{r.person_name}</div>
                      <div className="text-xs text-slate-400">{r.referring_agency} · {r.reviewed_by || "—"}</div>
                    </div>
                    <Badge value={r.status} />
                  </div>
                ))}
              </div>
            )}
          </Panel>
          <Panel title="Prosecutor tasks" testId="pw-tasks">
            {(d?.tasks || []).length === 0 ? (
              <div className="text-sm text-slate-400 p-3">Queue is clear.</div>
            ) : (
              <div className="divide-y divide-slate-200">
                {(d?.tasks || []).map((t) => (
                  <div key={t.id} className="px-3 py-2 text-sm">
                    <div className="text-slate-800">{t.title}</div>
                    <div className="text-xs text-slate-400">Due {t.due_date} · <Badge value={t.priority} /></div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

export function ChargingQueue({ referrals }) {
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const toggle = (r) => {
    if (open === r.id) { setOpen(null); return; }
    setOpen(r.id); setDetail(null);
    ujmsReferralDetail(r.id).then((res) => setDetail(res.data)).catch(() => {});
  };
  return (
    <Panel title="Charging review queue" testId="pw-charging-queue"
      action={<Link to="/ujms/criminal" className={btnGhost} data-testid="pw-open-pipeline">Open Criminal Pipeline</Link>}>
      {referrals.length === 0 ? (
        <EmptyState icon={Scale} title="No referrals awaiting decision" note="Law-enforcement referrals appear here for felony review." testId="pw-queue-empty" />
      ) : (
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Received</th><th className={thCls}>Subject</th><th className={thCls}>Agency</th><th className={thCls}>Proposed Counts</th><th className={thCls}></th></tr></thead>
          <tbody>
            {referrals.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="hover:bg-slate-50" data-testid={`pw-referral-${r.id}`}>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtD(r.created_at)}</td>
                  <td className={tdCls}>{r.person_name}</td>
                  <td className={tdCls}>{r.referring_agency}</td>
                  <td className={tdCls}>{(r.proposed_charges || []).length}</td>
                  <td className={tdCls}>
                    <button className="text-xs text-blue-900 underline" onClick={() => toggle(r)} data-testid={`pw-referral-review-${r.id}`}>
                      {open === r.id ? "Hide" : "Review"}
                    </button>
                  </td>
                </tr>
                {open === r.id && (
                  <tr>
                    <td colSpan={5} className="bg-slate-50 border-b border-slate-200 px-4 py-3" data-testid={`pw-referral-detail-${r.id}`}>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Proposed charges</div>
                          {(r.proposed_charges || []).map((c, i) => (
                            <div key={`${c.statute_code}-${i}`} className="text-slate-700">
                              <span className="font-mono text-xs">{c.statute_code}</span> — {c.description}
                              {c.classification && <span className="text-slate-400"> ({c.classification})</span>}
                            </div>
                          ))}
                          {r.notes && <div className="text-xs text-slate-500 mt-2">Referral notes: {r.notes}</div>}
                        </div>
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1">Arrest / subject</div>
                          {detail === null ? (
                            <div className="text-xs text-slate-400">Loading…</div>
                          ) : detail.arrest ? (
                            <div className="text-slate-700">
                              <div>{fmtD(detail.arrest.arrest_date)} — {detail.arrest.arresting_agency}</div>
                              {detail.arrest.location && <div className="text-xs text-slate-500">{detail.arrest.location}</div>}
                              {detail.arrest.narrative && <div className="text-xs text-slate-500 mt-1">{detail.arrest.narrative}</div>}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400">No linked arrest record.</div>
                          )}
                          <Link to="/ujms/criminal" className={`${btnPrimary} mt-3`} data-testid={`pw-decide-${r.id}`}>Decide in Pipeline</Link>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function CaseloadPanel({ onChanged }) {
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const load = useCallback(() => {
    const params = {};
    if (filter === "Mine") params.mine = true;
    if (filter === "Unassigned") params.unassigned = true;
    if (q.trim()) params.q = q.trim();
    ujmsProsecutorCaseload(params).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [filter, q]);
  useEffect(() => { load(); }, [load]);
  const [autoBusy, setAutoBusy] = useState(false);
  const autoAssign = async () => {
    if (!window.confirm("Assign a State's Attorney (round-robin) to every criminal/traffic case that has no lead prosecutor?")) return;
    setAutoBusy(true);
    try {
      const r = await ujmsProsecutorAutoAssign();
      toast.success(`Auto-assigned ${r.data.assigned} case(s) · ${r.data.already_assigned} already had a lead`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Auto-assign failed"); }
    finally { setAutoBusy(false); }
  };

  return (
    <Panel title="Criminal caseload" testId="pw-caseload"
      action={
        <div className="flex items-center gap-2">
          <button onClick={autoAssign} disabled={autoBusy} data-testid="pw-auto-assign-btn"
            className={cls(btnPrimary, "text-xs")}>{autoBusy ? "Assigning…" : "⚡ Auto-assign ASA to all"}</button>
          {CASE_FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`pw-caseload-filter-${f.toLowerCase()}`}
              className={cls("text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-sm border",
                filter === f ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")}>
              {f}
            </button>
          ))}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…"
            className="border border-slate-300 rounded-sm px-2 py-1 text-xs w-32" data-testid="pw-caseload-search" />
        </div>}>
      {!rows ? (
        <div className="text-sm text-slate-400 p-3">Loading caseload…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Briefcase} title="No matching cases" note="Adjust the filter or open new cases through intake." testId="pw-caseload-empty" />
      ) : (
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Case</th><th className={thCls}>Defendant</th><th className={thCls}>Lead ASA</th>
            <th className={thCls}>Charges</th><th className={thCls}>Next Hearing</th><th className={thCls}>Status</th><th className={thCls}></th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50" data-testid={`pw-case-${c.case_number}`}>
                <td className={`${tdCls} font-mono text-xs`}>
                  <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${c.id}`}>{c.case_number}</Link>
                  <div className="text-slate-400 truncate max-w-[220px]">{c.caption}</div>
                </td>
                <td className={tdCls}>{c.primary_defendant || "—"}</td>
                <td className={tdCls}>{c.lead_prosecutor || <span className="text-amber-700 text-xs font-semibold uppercase">Unassigned</span>}</td>
                <td className={`${tdCls} font-mono text-xs`}>{c.charges?.open}/{c.charges?.total}</td>
                <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{c.next_hearing ? fmtDT(c.next_hearing.scheduled_start) : "—"}</td>
                <td className={tdCls}><Badge value={c.status} /></td>
                <td className={tdCls}>
                  <button className={btnGhost} onClick={() => setAssigning(c)} data-testid={`pw-assign-${c.case_number}`}>
                    <UserPlus className="w-3.5 h-3.5" /> Assign
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <AssignDialog c={assigning} onClose={() => setAssigning(null)} onDone={() => { load(); onChanged(); }} />
    </Panel>
  );
}

export function AssignDialog({ c, onClose, onDone }) {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ assignee_user_id: "", role: "LEAD", reason: "" });
  useEffect(() => { if (c) ujmsProsecutorTeam().then((r) => setTeam(r.data)).catch(() => {}); }, [c]);
  const submit = async () => {
    try {
      await ujmsAssignProsecutor(c.id, form);
      toast.success(`PROSECUTOR_ASSIGNED — ${c.case_number}`);
      onClose(); onDone();
      setForm({ assignee_user_id: "", role: "LEAD", reason: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Assignment failed"); }
  };
  return (
    <Dialog open={!!c} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pw-assign-dialog">
        <DialogHeader><DialogTitle>Assign prosecutor — {c?.case_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Attorney" required>
            <select className={selectCls} value={form.assignee_user_id} data-testid="pw-assign-attorney"
              onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}>
              <option value="">Select attorney…</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Role">
            <select className={selectCls} value={form.role} data-testid="pw-assign-role"
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {["LEAD", "SECOND", "SUPPORT"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Reason / note">
            <input className={inputCls} value={form.reason} data-testid="pw-assign-reason"
              onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Felony review rotation…" />
          </Field>
          {c?.lead_prosecutor && form.role === "LEAD" && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5">
              Reassigning LEAD from {c.lead_prosecutor}. The previous assignment is closed, never deleted.
            </div>
          )}
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose} data-testid="pw-assign-cancel">Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={!form.assignee_user_id} data-testid="pw-assign-submit">Assign</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
